import { createAsyncThunk, createSelector, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { messagingApi } from "messaging/_api/messaging-api";
import ForumQuery, { ForumFilterParameters, ForumOrderParameters } from "messaging/forums/_api/queries/forum-query";
import Forum from "messaging/forums/forum";
import ForumEntityClass from "notifications/message-notifications/forum-info/forum-entity-class";
import Session from "users/__session/session";
import {
  editComment,
  populateThreadsComments,
  postComment,
  publishComment,
  saveDraftComment,
  unpublishComment,
} from "work/proposals/comments/_store/comments-redux-slice";
import { CommentThreadJSON } from "work/proposals/comments/threads/_schemas/comment-thread-schema";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import Proposal from "work/proposals/proposal";

type CommentThreadsStoreState = {
  byProposalId: {
    entries: Record<string, CommentThreadJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
};

const initialState: CommentThreadsStoreState = {
  byProposalId: {
    entries: {},
    loading: {},
    error: {},
  },
};
export const populateCommentThreadsByProposal = createAsyncThunk(
  "commentThreads/getCommentThreadsByProposal",
  async ({ session, proposal, proposalId }: { session: Session; proposal?: Proposal; proposalId?: Guid }, thunkAPI) => {
    const id: Guid | undefined = proposal?.id ?? proposalId;
    if (!id) return thunkAPI.rejectWithValue("Proposal id is required");
    try {
      const filterParameters = new ForumFilterParameters({
        entityClass: new ForumEntityClass("Work.Proposal"),
        entityId: id,
      });
      const orderParameters = new ForumOrderParameters({});
      const paginationParameters = PaginationParameters.create({ pageIndex: 0, pageSize: 25 });
      const request = new ForumQuery({
        filterParameters,
        orderParameters,
        paginationParameters,
      });
      const apiRequest = thunkAPI.dispatch(
        messagingApi.endpoints.getForums.initiate({
          query: request,
          storeTarget: { kind: "commentThreadsByProposal", proposalId: id.value },
        }),
      );
      const onAbort = () => apiRequest.abort();
      thunkAPI.signal.addEventListener("abort", onAbort);
      try {
        const forumJSON = await apiRequest.unwrap();
        const forums = Forum.fromJSONArray(forumJSON) ?? [];
        const threads = forums.map((forum) => CommentThread.fromForum(forum));
        // Eager-load every thread's comment bodies the moment the threads enter the store, in one
        // batched request rather than one `getMessagesByForum` per thread. Comment content is otherwise
        // fetched lazily on first drawer open, during which the list is replaced by a <Loader/>; the
        // issue traverser (and a user quickly stepping through threads) can open a field, read
        // "Loading…" before that first fetch settles, and move on — never seeing the comment.
        // Pre-loading here means every field's comments are already in the store before any drawer
        // opens, so the drawer renders them immediately and consistently. Fire-and-forget: the thread
        // list and its unread badges must not wait on comment bodies.
        if (threads.length > 0) {
          void thunkAPI.dispatch(populateThreadsComments({ session, threads }));
        }
        return threads.map((thread) => thread.toJSON());
      } finally {
        thunkAPI.signal.removeEventListener("abort", onAbort);
        apiRequest.unsubscribe();
      }
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    }
  },
);
const commentThreadsSlice = createSlice({
  name: "CommentThreads",
  initialState,
  reducers: {
    addCommentThread: (state, action: PayloadAction<CommentThread>) => {
      if (action.payload.id) {
        const existingThreads = state.byProposalId.entries[action.payload.proposalId.value] ?? [];
        const newThreads = existingThreads.filter((thread) => thread.id !== action.payload.id?.value);
        newThreads.push(action.payload.toJSON());
        state.byProposalId.entries[action.payload.proposalId.value] = newThreads;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(populateCommentThreadsByProposal.pending, (state, action) => {
      const proposalId = action.meta.arg.proposal?.id?.value ?? action.meta.arg.proposalId?.value;
      if (!proposalId) {
        console.warn("Proposal id not referenced in populateCommentThreadsByProposal");
        return;
      }
      state.byProposalId.loading[proposalId] = true;
      state.byProposalId.error[proposalId] = null;
    });
    builder.addCase(populateCommentThreadsByProposal.fulfilled, (state, action) => {
      const proposalIdValue = action.meta.arg.proposalId?.value ?? action.meta.arg.proposal?.id?.value;
      if (!proposalIdValue) throw new Error("No proposal id provided for populating threads");

      state.byProposalId.loading[proposalIdValue] = false;
      state.byProposalId.entries[proposalIdValue] = action.payload;
    });
    builder.addCase(populateCommentThreadsByProposal.rejected, (state, action) => {
      const proposalId = action.meta.arg.proposal?.id?.value ?? action.meta.arg.proposalId?.value;
      if (!proposalId) {
        console.warn("Proposal id not referenced in populateCommentThreadsByProposal");
        return;
      }
      state.byProposalId.loading[proposalId] = false;
      state.byProposalId.error[proposalId] = action.error;
    });
    // The thread a comment write resolves to is the server's answer about where that comment now lives —
    // including a re-aimed comment, which the server re-homes onto the counterparty-facing Forum. This
    // used to be fed only by the proposal Save's bulk write; it is fed by each comment's own write now,
    // so the thread list keeps up with a conversation that no longer waits for a save.
    for (const singleCommentThunk of [
      postComment,
      saveDraftComment,
      editComment,
      publishComment,
      unpublishComment,
    ]) {
      builder.addCase(singleCommentThunk.fulfilled, (state, action) => {
        const thread = action.payload?.thread;
        if (!thread) return;

        const existingThreads = state.byProposalId.entries[thread.proposalId.value] ?? ([] as CommentThread[]);
        const newThreads = existingThreads.filter((t) => t.id !== thread.id?.value);
        newThreads.push(thread.toJSON());
        state.byProposalId.entries[thread.proposalId.value] = newThreads;
      });
    }
    builder.addMatcher(messagingApi.endpoints.getForums.matchPending, (state, action) => {
      const storeTarget = action.meta.arg.originalArgs.storeTarget;
      if (storeTarget?.kind !== "commentThreadsByProposal") return;
      state.byProposalId.loading[storeTarget.proposalId] = true;
      state.byProposalId.error[storeTarget.proposalId] = null;
    });
    builder.addMatcher(messagingApi.endpoints.getForums.matchFulfilled, (state, action) => {
      const storeTarget = action.meta.arg.originalArgs.storeTarget;
      if (storeTarget?.kind !== "commentThreadsByProposal") return;
      const forums = Forum.fromJSONArray(action.payload) ?? [];
      state.byProposalId.loading[storeTarget.proposalId] = false;
      state.byProposalId.entries[storeTarget.proposalId] = forums.map((forum) => CommentThread.fromForum(forum).toJSON());
    });
    builder.addMatcher(messagingApi.endpoints.getForums.matchRejected, (state, action) => {
      const storeTarget = action.meta.arg.originalArgs.storeTarget;
      if (storeTarget?.kind !== "commentThreadsByProposal") return;
      state.byProposalId.loading[storeTarget.proposalId] = false;
      state.byProposalId.error[storeTarget.proposalId] = action.error;
    });
  },
});

export const { addCommentThread } = commentThreadsSlice.actions;

const selectCommentThreadsByProposalId = createSelector(
  [
    (state: CommentThreadsStoreState) => state.byProposalId.entries,
    (_: CommentThreadsStoreState, proposalId: Guid | null | undefined) => proposalId,
  ],
  (entries, proposalId) => {
    if (!proposalId) return;
    return entries[proposalId.value]
      ?.map((threadJson) => CommentThread.fromJSON(threadJson))
      .filter((thread): thread is CommentThread => thread !== null);
  },
);
export const getCommentThreadsByProposal =
  (proposal?: Proposal | null, proposalId?: Guid | null) => (state: RootState) =>
    selectCommentThreadsByProposalId(state.commentThreads, proposal?.id ?? proposalId);

export const getIsLoadingCommentThreadsByProposal = (proposal?: Proposal | null, proposalId?: Guid | null) => {
  return (state: RootState) => {
    const id = proposal?.id?.value ?? proposalId?.value;
    if (!id) return false;
    return state.commentThreads?.byProposalId.loading[id];
  };
};
export const getErrorLoadingCommentThreadsByProposal = (proposal?: Proposal | null, proposalId?: Guid | null) => {
  return (state: RootState) => {
    const id = proposal?.id?.value ?? proposalId?.value;
    if (!id) return undefined;
    return state.commentThreads?.byProposalId.error[id];
  };
};

export default commentThreadsSlice;
